import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import InfoBadge from "@/components/base/InfoBadge";
import ArticleCard from "@/components/feature/ArticleCard";
import useSeo from "@/hooks/useSeo";
import { articles } from "@/mocks/articles";
import { getCategoryName, getRelated, formatDate } from "@/utils/content";

export default function ArticlePage() {
  const { id = "" } = useParams();
  const article = articles.find((a) => a.id === id);

  const headings = article
    ? article.content.filter((b) => b.type === "h2").map((b, i) => ({ id: `section-${i}`, text: b.text ?? "" }))
    : [];

  const related = article ? getRelated(article, articles, 3) : [];

  useSeo({
    title: article
      ? `${article.title}｜灵灵事件信息梳理`
      : "文章不存在｜灵灵事件信息梳理站",
    description: article
      ? article.excerpt.slice(0, 150)
      : "未找到该文章，请返回首页查看灵灵事件的完整梳理内容。",
    keywords: article ? `${article.tags.join(",")},灵灵黑料,灵灵后续进展` : "灵灵黑料,灵灵事件梳理",
    path: `/article/${id}`,
    jsonLd: article
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.excerpt,
            datePublished: article.date,
            dateModified: article.date,
            author: { "@type": "Organization", name: article.author },
            publisher: { "@type": "Organization", name: "灵灵事件信息梳理站" },
            inLanguage: "zh-CN",
            keywords: article.tags.join(","),
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页" },
              { "@type": "ListItem", position: 2, name: getCategoryName(article.category) },
              { "@type": "ListItem", position: 3, name: article.title },
            ],
          },
        ]
      : undefined,
  });

  if (!article) {
    return (
      <SiteLayout>
        <div className="mx-auto max-w-content px-4 py-20 text-center md:px-6">
          <i className="ri-file-unknow-line text-4xl text-foreground-400" aria-hidden="true"></i>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">这篇内容不存在</h1>
          <p className="mt-2 text-sm text-foreground-600">链接可能已失效，回到首页可以查看全部梳理内容。</p>
          <Link
            to="/"
            className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  let headingIndex = -1;

  return (
    <SiteLayout>
      <article className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-10">
        <Breadcrumb
          items={[
            { label: getCategoryName(article.category), to: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <header className="mx-auto max-w-3xl">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/category/${article.category}`}
              className="cursor-pointer rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700 hover:bg-primary-100"
            >
              {getCategoryName(article.category)}
            </Link>
            <InfoBadge level={article.infoLevel} />
          </div>
          <h1 className="mt-4 font-heading text-2xl font-black leading-tight text-foreground-950 md:text-4xl">
            {article.title}
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">{article.excerpt}</p>
          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-background-200 py-3 text-xs text-foreground-500 md:text-sm">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line" aria-hidden="true"></i>
              {article.author}
            </span>
            <time className="flex items-center gap-1.5">
              <i className="ri-calendar-line" aria-hidden="true"></i>
              {formatDate(article.date)}
            </time>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line" aria-hidden="true"></i>
              阅读约 {article.readTime}
            </span>
          </div>
        </header>

        <figure className="mx-auto mt-6 max-w-4xl overflow-hidden rounded-lg border border-background-200">
          <div className="h-56 w-full md:h-80">
            <img
              src={article.cover}
              alt={`${article.title} - 灵灵事件信息梳理配图`}
              title={`${article.title}｜灵灵事件梳理`}
              className="h-full w-full object-cover object-top"
            />
          </div>
        </figure>

        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-8">
            <div className="max-w-3xl">
              {article.content.map((block, i) => {
                if (block.type === "h2") {
                  headingIndex += 1;
                  return (
                    <h2
                      key={`h2-${i}`}
                      id={`section-${headingIndex}`}
                      className="mt-9 scroll-mt-28 font-heading text-xl font-bold text-foreground-950 md:text-2xl"
                    >
                      {block.text}
                    </h2>
                  );
                }
                if (block.type === "list") {
                  return (
                    <ul key={`list-${i}`} className="mt-4 space-y-2">
                      {(block.items ?? []).map((item) => (
                        <li key={item} className="flex items-start gap-2.5 text-sm leading-relaxed text-foreground-700 md:text-base">
                          <i className="ri-checkbox-blank-circle-fill mt-2 text-[6px] text-primary-500" aria-hidden="true"></i>
                          {item}
                        </li>
                      ))}
                    </ul>
                  );
                }
                if (block.type === "quote") {
                  return (
                    <blockquote
                      key={`quote-${i}`}
                      className="mt-6 rounded-lg border-l-4 border-accent-500 bg-accent-50 px-5 py-4"
                    >
                      <p className="font-heading text-sm leading-relaxed text-foreground-900 md:text-base">
                        {block.text}
                      </p>
                    </blockquote>
                  );
                }
                return (
                  <p key={`p-${i}`} className="mt-4 text-sm leading-7 text-foreground-700 md:text-base md:leading-8">
                    {block.text}
                  </p>
                );
              })}
            </div>

            <div className="mt-8 flex flex-wrap gap-2">
              {article.tags.map((t) => (
                <span
                  key={t}
                  className="rounded-full border border-secondary-200 bg-secondary-50 px-3 py-1 text-xs text-secondary-900"
                >
                  #{t}
                </span>
              ))}
            </div>

            <aside className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-900">
                <i className="ri-alert-line text-primary-500" aria-hidden="true"></i>
                信息说明
              </h2>
              <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                本文内容整理自公开渠道，标注等级仅代表当前可核验程度，不构成对任何一方的定性结论。事件仍在发展，请以当事人及相关平台的官方最新公告为准。
              </p>
            </aside>
          </div>

          <aside className="lg:col-span-4">
            <div className="sticky top-28 space-y-6">
              {headings.length > 0 && (
                <nav aria-label="文章目录" className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="font-heading text-sm font-bold text-foreground-950">本文目录</h2>
                  <ul className="mt-3 space-y-1">
                    {headings.map((h, i) => (
                      <li key={h.id}>
                        <a
                          href={`#${h.id}`}
                          className="flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 text-xs leading-snug text-foreground-600 transition-colors hover:bg-background-100 hover:text-primary-700"
                        >
                          <span className="font-heading font-bold text-primary-400">{String(i + 1).padStart(2, "0")}</span>
                          {h.text}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>
              )}

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-sm font-bold text-foreground-950">相关内容推荐</h2>
                <div className="mt-2">
                  {related.map((a) => (
                    <ArticleCard key={a.id} article={a} variant="compact" />
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-primary-200 bg-primary-50 p-5">
                <h2 className="font-heading text-sm font-bold text-primary-800">还在找完整脉络？</h2>
                <p className="mt-2 text-xs leading-relaxed text-primary-900/80">
                  时间线页面把全部可核验节点按顺序排好，比单篇阅读更快建立全局印象。
                </p>
                <Link
                  to="/article/timeline-full"
                  className="mt-3 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  查看时间线
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </article>
    </SiteLayout>
  );
}